import React, { useState, useEffect, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import { API_get_timecard_hours_split } from "../../../../../constants";
import { getRightworkAPI } from "../../../requests/utilities/requests";
import DateSelector from "../../../plan/daily_top_bar_components/DateSelector";
import PrebuiltReportShell from "../PrebuiltReportShell";
import ReportCsvDownloadButton from "../ReportCsvDownloadButton";
import ReportEmailToggle from "../ReportEmailToggle";
import {
  displayName,
  formatCurrency,
  formatHours,
} from "./_shared/laborMath";
import { lastCompletedBusinessDay } from "./_shared/reportDates";

// FLSA-style multipliers. We apply them on the frontend for now; once the
// backend can supply pay-rate-aware splits we can move this into the helper.
const PAY_MULTIPLIER = {
  regular: 1,
  overtime: 1.5,
  doubletime: 2,
  holiday: 1,
};

// Single source of truth for the table layout. Adding/removing/reordering
// columns means updating this list, the <thead>, the body cells, and the
// TotalRow's cell count — keep them in sync.
const COLUMN_WIDTHS = [
  "60px",  // ID
  "180px", // Employee
  "80px",  // Clock In
  "80px",  // Clock Out
  "60px",  // Reg
  "60px",  // OT
  "60px",  // DT
  "60px",  // Hol
  "70px",  // Total Hrs
  "70px",  // Paid Brk
  "70px",  // Unpaid Brk
  "60px",  // Rate
  "80px",  // Reg Pay
  "80px",  // OT Pay
  "80px",  // DT Pay
  "80px",  // Hol Pay
  "80px",  // Special
  "90px",  // Total Pay
  "70px",  // CC Tips
  "70px",  // Decl Tips
];
const TOTAL_COL_COUNT = COLUMN_WIDTHS.length; // 20

function PunchAuditColGroup() {
  return (
    <colgroup>
      {COLUMN_WIDTHS.map((w, i) => (
        <col key={i} style={{ width: w }} />
      ))}
    </colgroup>
  );
}

function payFor(hours, hourlyWage, kind) {
  if (hours == null || hourlyWage == null) return null;
  const h = Number(hours);
  const w = Number(hourlyWage);
  if (Number.isNaN(h) || Number.isNaN(w)) return null;
  return h * w * (PAY_MULTIPLIER[kind] ?? 1);
}

function NotAvailable() {
  return <span className="text-slate-300">—</span>;
}

function emptyTotal() {
  return {
    shifts: 0,
    reg: 0,
    ot: 0,
    dt: 0,
    hol: 0,
    total: 0,
    paidBreak: 0,
    unpaidBreak: 0,
    regPay: 0,
    otPay: 0,
    dtPay: 0,
    holPay: 0,
    specialPay: 0,
    totalPay: 0,
  };
}

function addToTotal(target, row) {
  target.shifts += 1;
  target.reg += row.reg ?? 0;
  target.ot += row.ot ?? 0;
  target.dt += row.dt ?? 0;
  target.hol += row.hol ?? 0;
  target.total += row.total ?? 0;
  target.paidBreak += row.paidBreakHrs ?? 0;
  target.unpaidBreak += row.unpaidBreakHrs ?? 0;
  target.regPay += row.regPay ?? 0;
  target.otPay += row.otPay ?? 0;
  target.dtPay += row.dtPay ?? 0;
  target.holPay += row.holPay ?? 0;
  target.specialPay += row.specialPay ?? 0;
  target.totalPay += row.totalPay ?? 0;
}

// 4 left-side cells (ID · Employee · Clock In · Clock Out) merge into the
// label cell on totals rows. Remaining 16 cells render numeric totals or
// placeholders.
const TOTAL_LABEL_COLSPAN = 4;

function TotalRow({ label, total, strong = false }) {
  const cellClass = strong
    ? "px-3 py-2 text-right font-bold text-slate-900"
    : "px-3 py-2 text-right font-semibold text-slate-800";
  return (
    <tr className={strong ? "bg-slate-100" : "bg-slate-50"}>
      <td
        colSpan={TOTAL_LABEL_COLSPAN}
        className={`px-3 py-2 text-right ${
          strong ? "font-bold text-slate-900" : "font-semibold text-slate-700"
        }`}
      >
        {label}
      </td>
      <td className={cellClass}>{formatHours(total.reg)}</td>
      <td className={cellClass}>{formatHours(total.ot)}</td>
      <td className={cellClass}>{formatHours(total.dt)}</td>
      <td className={cellClass}>{formatHours(total.hol)}</td>
      <td className={cellClass}>{formatHours(total.total)}</td>
      <td className={cellClass}>{formatHours(total.paidBreak)}</td>
      <td className={cellClass}>{formatHours(total.unpaidBreak)}</td>
      <td className={cellClass}>—</td>
      <td className={cellClass}>{formatCurrency(total.regPay)}</td>
      <td className={cellClass}>{formatCurrency(total.otPay)}</td>
      <td className={cellClass}>{formatCurrency(total.dtPay)}</td>
      <td className={cellClass}>{formatCurrency(total.holPay)}</td>
      <td className={cellClass}>
        {total.specialPay > 0 ? formatCurrency(total.specialPay) : "—"}
      </td>
      <td className={cellClass}>{formatCurrency(total.totalPay)}</td>
      <td className={cellClass}>—</td>
      <td className={cellClass}>—</td>
    </tr>
  );
}

export default function DailyPunchAuditReport({
  report,
  onBack,
  storePicker,
  homeStore,
  storeNames,
  userId,
}) {
  // Opens on the store's last completed business day: the report is built
  // from actuals, which aren't complete until the business day ends.
  const [selectedDate, setSelectedDate] = useState(() =>
    lastCompletedBusinessDay(storeNames, homeStore)
  );

  const incrementDay = useCallback((current, days) => {
    const next = new Date(current);
    next.setDate(next.getDate() + days);
    next.setHours(0, 0, 0, 0);
    setSelectedDate(next);
  }, []);

  const dateStr = useMemo(
    () => dayjs(selectedDate).format("YYYY-MM-DD"),
    [selectedDate]
  );

  const [timecards, setTimecards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (!homeStore) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await getRightworkAPI(API_get_timecard_hours_split, {
          store_id: homeStore,
          date_start: dateStr,
          date_end: dateStr,
        });
        if (cancelled) return;
        const list = Array.isArray(res?.data?.timecards)
          ? res.data.timecards
          : [];
        setTimecards(list);
      } catch (e) {
        if (cancelled) return;
        setError("Couldn't load punches. Try again or pick another day.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [homeStore, dateStr, retryNonce]);

  const handleRetry = useCallback(() => setRetryNonce((n) => n + 1), []);

  // Compute derived row data + nested grouping (work center → job → rows)
  // in one pass so the totals are consistent with what's rendered.
  const data = useMemo(() => {
    // workCenter → jobName → row[]
    const tree = new Map();
    const wcTotals = new Map();
    const jobTotals = new Map(); // key: `${wc}::${job}` → total
    const grandTotal = emptyTotal();

    const ensure = (map, key, init) => {
      if (!map.has(key)) map.set(key, init());
      return map.get(key);
    };

    for (const tc of timecards) {
      const wc = tc.work_center || "—";
      const job = tc.role?.role_name || "—";
      const stm = tc.store_team_member;
      // `effective_hourly_wage` is the payroll-grade per-shift wage that the
      // backend already looked up via StoreTeamMemberWage (role-specific
      // override, falls back to general). Null means no matching hourly wage
      // record exists for this employee as of the report date.
      const wageRaw =
        tc.effective_hourly_wage != null && tc.effective_hourly_wage !== ""
          ? Number(tc.effective_hourly_wage)
          : NaN;
      const wage = Number.isFinite(wageRaw) && wageRaw > 0 ? wageRaw : null;

      const reg = Number(tc.regular ?? 0);
      const ot = Number(tc.overtime ?? 0);
      const dt = Number(tc.doubletime ?? 0);
      const hol = Number(tc.holiday ?? 0);
      const total = Number(tc.total_paid_hours ?? reg + ot + dt + hol);

      // Break aggregation by paid/unpaid (in hours)
      let paidBreakMin = 0;
      let unpaidBreakMin = 0;
      for (const b of tc.breaks || []) {
        const mins = Number(b.duration_minutes ?? 0);
        if (Number.isNaN(mins)) continue;
        if (b.is_paid) paidBreakMin += mins;
        else unpaidBreakMin += mins;
      }
      const paidBreakHrs = paidBreakMin / 60;
      const unpaidBreakHrs = unpaidBreakMin / 60;

      const regPay = payFor(reg, wage, "regular") ?? 0;
      const otPay = payFor(ot, wage, "overtime") ?? 0;
      const dtPay = payFor(dt, wage, "doubletime") ?? 0;
      const holPay = payFor(hol, wage, "holiday") ?? 0;

      // Premium-pay hours from the backend labor-law subcheckers.
      //   - MBP (CA Labor Code 226.7): paid at the employee's regular rate.
      //   - SSP (CA wage order):       paid at minimum wage, NOT regular.
      // We don't have minimum-wage data in the system, so we can't compute
      // SSP $ correctly. Multiplying SSP × regular wage overstates pay for
      // anyone above min wage — so we deliberately leave SSP out of the
      // Special $ rollup and Total Pay, and surface the SSP hours in the
      // tooltip + caveat so managers know to use the Paylocity export for
      // the authoritative SSP $.
      const mbpHrs = Number(tc.meal_break_premium_hours ?? 0) || 0;
      const sspHrs = Number(tc.split_shift_premium_hours ?? 0) || 0;
      const mbpPay = wage != null && mbpHrs > 0 ? mbpHrs * wage : 0;
      const specialPay = mbpPay;

      const totalPay = regPay + otPay + dtPay + holPay + mbpPay;

      const row = {
        key: `tc-${tc.id}`,
        id: tc.id,
        // ID column shows the customer's configured payroll-system
        // employee ID (UserCompanyProfile.payroll_external_id) so the
        // report reconciles against payroll. Falls back to the internal
        // STM id only when no external id is on file.
        employeeId: stm?.payroll_external_id || stm?.id || null,
        // Unmatched POS punches have stm = null; surface them as
        // "Unmatched (POS)" instead of "—" so managers can spot the rows
        // and fix the employee mapping. Hours still roll up correctly.
        employee: stm?.id ? displayName(stm) : "Unmatched (POS)",
        clockIn: tc.clocked_in_at,
        clockOut: tc.clocked_out_at,
        reg,
        ot,
        dt,
        hol,
        total,
        paidBreakHrs,
        unpaidBreakHrs,
        wage,
        regPay,
        otPay,
        dtPay,
        holPay,
        mbpHrs,
        sspHrs,
        specialPay,
        totalPay,
      };

      // Build the tree
      const wcBranch = ensure(tree, wc, () => new Map());
      const jobRows = ensure(wcBranch, job, () => []);
      jobRows.push(row);

      // Totals
      const wcKey = wc;
      const jobKey = `${wc}::${job}`;
      addToTotal(ensure(wcTotals, wcKey, emptyTotal), row);
      addToTotal(ensure(jobTotals, jobKey, emptyTotal), row);
      addToTotal(grandTotal, row);
    }

    // Flatten the Maps into arrays, sorted by name.
    const groups = Array.from(tree.entries())
      .map(([wc, jobMap]) => ({
        workCenter: wc,
        wcTotal: wcTotals.get(wc),
        jobs: Array.from(jobMap.entries())
          .map(([job, rows]) => ({
            job,
            rows: rows
              .slice()
              .sort((a, b) => a.employee.localeCompare(b.employee)),
            jobTotal: jobTotals.get(`${wc}::${job}`),
          }))
          .sort((a, b) => a.job.localeCompare(b.job)),
      }))
      .sort((a, b) => a.workCenter.localeCompare(b.workCenter));

    return { groups, grandTotal };
  }, [timecards]);

  const filters = (
    <div className="flex items-center">
      <DateSelector date={selectedDate} increment_day={incrementDay} />
    </div>
  );

  const fullDateLabel = dayjs(selectedDate).format("dddd, MMMM D, YYYY");

  return (
    <PrebuiltReportShell
      actions={
        <>
          <ReportEmailToggle
            reportId={report.id}
            userId={userId}
            schedule={report.emailSchedule}
          />
          <ReportCsvDownloadButton
            reportId="daily-punch-audit"
            storeId={homeStore}
            start={dateStr}
            end={dateStr}
          />
        </>
      }
      report={report}
      onBack={onBack}
      filters={filters}
      storePicker={storePicker}
      isLoading={isLoading}
    >
      {error ? (
        <div className="py-6 text-center">
          <div className="text-sm text-red-600 mb-3">{error}</div>
          <button
            type="button"
            onClick={handleRetry}
            className="text-xs px-3 py-1.5 rounded border border-violet-600 text-violet-700 hover:bg-violet-50"
          >
            Retry
          </button>
        </div>
      ) : data.groups.length === 0 ? (
        <div className="text-sm text-gray-500 py-8 text-center border border-slate-200 rounded-lg">
          No completed punches on this day.
        </div>
      ) : (
        <>
          <div className="mb-3">
            <h4 className="text-sm font-semibold text-slate-700">
              {fullDateLabel}
            </h4>
            <p className="text-xs text-slate-500">
              Per-shift punch detail with regular/OT/DT hours, paid vs. unpaid
              breaks, and pay rollups — grouped by work center and job.
            </p>
          </div>

          {data.groups.map(({ workCenter, wcTotal, jobs }) => (
            <div
              key={workCenter}
              className="border border-slate-200 rounded-lg overflow-hidden mb-4"
            >
              <div className="bg-slate-100 border-b border-slate-200 px-4 py-2">
                <h5 className="text-sm font-semibold text-slate-800">
                  {workCenter || "—"}
                </h5>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs table-fixed">
                  <PunchAuditColGroup />
                  <thead className="bg-white border-b border-slate-100 text-slate-500">
                    <tr className="text-[10px]">
                      <th className="text-left font-semibold px-3 py-1.5">ID</th>
                      <th className="text-left font-semibold px-3 py-1.5">
                        Employee
                      </th>
                      <th className="text-left font-semibold px-3 py-1.5">
                        Clock In
                      </th>
                      <th className="text-left font-semibold px-3 py-1.5">
                        Clock Out
                      </th>
                      <th className="text-right font-semibold px-3 py-1.5">
                        Reg
                      </th>
                      <th className="text-right font-semibold px-3 py-1.5">
                        OT
                      </th>
                      <th className="text-right font-semibold px-3 py-1.5">
                        DT
                      </th>
                      <th className="text-right font-semibold px-3 py-1.5">
                        Hol
                      </th>
                      <th className="text-right font-semibold px-3 py-1.5">
                        Total Hrs
                      </th>
                      <th className="text-right font-semibold px-3 py-1.5">
                        Paid Brk
                      </th>
                      <th className="text-right font-semibold px-3 py-1.5">
                        Unpaid Brk
                      </th>
                      <th className="text-right font-semibold px-3 py-1.5">
                        Rate
                      </th>
                      <th className="text-right font-semibold px-3 py-1.5">
                        Reg Pay
                      </th>
                      <th className="text-right font-semibold px-3 py-1.5">
                        OT Pay
                      </th>
                      <th className="text-right font-semibold px-3 py-1.5">
                        DT Pay
                      </th>
                      <th className="text-right font-semibold px-3 py-1.5">
                        Hol Pay
                      </th>
                      <th className="text-right font-semibold px-3 py-1.5">
                        Special
                      </th>
                      <th className="text-right font-semibold px-3 py-1.5">
                        Total Pay
                      </th>
                      <th className="text-right font-semibold px-3 py-1.5">
                        CC Tips
                      </th>
                      <th className="text-right font-semibold px-3 py-1.5">
                        Decl Tips
                      </th>
                    </tr>
                  </thead>
                  {jobs.map(({ job, rows, jobTotal }) => (
                    <tbody key={job} className="border-t border-slate-200">
                      <tr className="bg-slate-50">
                        <td
                          colSpan={TOTAL_COL_COUNT}
                          className="px-3 py-1.5 font-semibold text-xs text-slate-800"
                        >
                          {job}
                          <span className="ml-2 text-[10px] text-slate-500 font-normal">
                            {rows.length} shift
                            {rows.length === 1 ? "" : "s"}
                          </span>
                        </td>
                      </tr>
                      {rows.map((r) => (
                        <tr
                          key={r.key}
                          className="border-b border-slate-50 hover:bg-slate-50"
                        >
                          <td className="px-3 py-2 text-slate-500 truncate">
                            {r.employeeId ?? "—"}
                          </td>
                          <td className="px-3 py-2 font-medium text-slate-800 truncate">
                            {r.employee}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {r.clockIn || "—"}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {r.clockOut || "—"}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {formatHours(r.reg)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {formatHours(r.ot)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {formatHours(r.dt)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {formatHours(r.hol)}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-800">
                            {formatHours(r.total)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {formatHours(r.paidBreakHrs)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {formatHours(r.unpaidBreakHrs)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {r.wage == null
                              ? "—"
                              : formatCurrency(r.wage)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {formatCurrency(r.regPay)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {formatCurrency(r.otPay)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {formatCurrency(r.dtPay)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {formatCurrency(r.holPay)}
                          </td>
                          <td
                            className="px-3 py-2 text-right text-slate-700"
                            title={
                              r.mbpHrs > 0 || r.sspHrs > 0
                                ? `MBP ${formatHours(r.mbpHrs)} hr (at regular rate)\nSSP ${formatHours(r.sspHrs)} hr (paid at min wage; see your payroll system for $)`
                                : undefined
                            }
                          >
                            {r.specialPay > 0 ? (
                              formatCurrency(r.specialPay)
                            ) : (
                              <NotAvailable />
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-900">
                            {formatCurrency(r.totalPay)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-400">
                            <NotAvailable />
                          </td>
                          <td className="px-3 py-2 text-right text-slate-400">
                            <NotAvailable />
                          </td>
                        </tr>
                      ))}
                      <TotalRow label={`${job} Total`} total={jobTotal} />
                    </tbody>
                  ))}
                  <tfoot>
                    <TotalRow
                      label={`${workCenter} Total`}
                      total={wcTotal}
                      strong
                    />
                  </tfoot>
                </table>
              </div>
            </div>
          ))}

          {/* Grand total card */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-100 border-b border-slate-200 px-4 py-2">
              <h5 className="text-sm font-semibold text-slate-800">
                Day Total
              </h5>
              <p className="text-[10px] text-slate-500 mt-0.5">
                {data.grandTotal.shifts} shift
                {data.grandTotal.shifts === 1 ? "" : "s"} across all jobs. Pay
                uses each shift's role-specific wage (general-wage fallback) at
                FLSA multipliers (Reg 1×, OT 1.5×, DT 2×). Special Pay reflects
                Meal Break Premiums only (paid at the regular rate per CA Labor
                Code 226.7). Split Shift Premium hours are surfaced in the
                tooltip but excluded from $ totals — CA pays SSP at minimum
                wage, so check your payroll system for the SSP amount. Verify
                tips, Rest Break Premium, and FLSA blended-rate adjustments for
                multi-role workweeks in payroll.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs table-fixed">
                <PunchAuditColGroup />
                <tbody>
                  <TotalRow label="Grand Total" total={data.grandTotal} strong />
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </PrebuiltReportShell>
  );
}
